import { useAuthStore } from '~app/core/auth/store';
import { NavigationGuard } from 'vue-router';
import { createError } from '~app/core/error';
import { AccountModel } from '~app/modules/account/types';
import { isNullOrUndefined } from '~app/shared/json-mapper/utils';

export const REQUESTED_USER_ID_PARAM = 'sub';

export const authGuard: NavigationGuard = (to, from, next) => {
  const authStore = useAuthStore();
  const isPublic = to.matched.some((r) => r.meta.public);
  const requestedUserId = to.query[REQUESTED_USER_ID_PARAM];
  const targetUrl = new URL(to.fullPath, location.origin);
  let authPromise: Promise<void | AccountModel> = Promise.resolve();

  if (isPublic) {
    return next();
  }

  if (!authStore.isAuthenticated) {
    targetUrl.searchParams.delete(REQUESTED_USER_ID_PARAM);
    return authStore.loginWithRedirect({
      targetUrl: targetUrl.toString(),
    });
  }

  if (!isNullOrUndefined(requestedUserId) && requestedUserId !== authStore.currentUserId) {
    return authStore.loginWithRedirect({ targetUrl: targetUrl.toString() });
  }

  if (!authStore.user) {
    authPromise = authStore.fetchUser();
  }

  authPromise
    .then(() => {
      next();
    })
    .catch((err) => {
      next(createError(err));
    });
};

export const ssoRedirectCallbackGuard: NavigationGuard = ({ query }, from, next) => {
  const {
    code,
    error,
    error_description: errorDescription,
    state,
  } = query as Record<string, string>;
  const authStore = useAuthStore();

  authStore
    .handleRedirectCallback({
      code,
      error,
      errorDescription,
      state,
    })
    .then((targetUrl) => {
      if (!targetUrl) {
        return next({ path: '/' });
      }

      const url = new URL(targetUrl);
      const requestedUserId = url.searchParams.get(REQUESTED_USER_ID_PARAM);

      if (!isNullOrUndefined(requestedUserId) && requestedUserId !== authStore.currentUserId) {
        return authStore.logout();
      }

      return next({ path: url.pathname, query: Object.fromEntries(url.searchParams.entries()) });
    })
    .catch((err) => {
      next(createError(err));
    });
};
